// Copyright 2026, Common Good Learning Tools LLC
import { mapState, mapGetters } from 'vuex'

export default {
	data() { return {
		job_status: null,
		resource_universe: null,
		comp_universe: null,
		resource_aggregation_type: '',

		// note that the matrix and sub_matrix are indexed first by standard (comp_id), then by chapter (resource_id)
		matrix: null,
		sub_matrix: null,
		// components that never edit matrix cells can set this to true; load_data then freezes the matrices
		// so Vue doesn't make every cell reactive (which is very slow for large matrices)
		matrix_read_only: false,
		rfr: null,
		cfr: null,

		comp_framework_id:'',
		resource_framework_id:'',

		resource_branch_showing: [],

		// the computed for `alignments` (which is in turn called by n_alignments) computes the below data items as a side-effect
		resources_aligned: null,
		comps_aligned: null,
		n_primary_alignments: null,
		n_sme_alignments: null,
		n_auto_alignments: null,
		n_not_aligned: null,
	}},
	computed: {
		...mapState(['user_info']),
		...mapGetters([]),

		align_job() { 
			if (this.job_type) return this.job_type == 'align'
			return (this.job_status?.job_type) == 'align' 
		},
		crosswalk_job() { 
			if (this.job_type) return this.job_type == 'crosswalk'
			return (this.job_status?.job_type) == 'crosswalk' 
		},

		ru_label() {
			if (this.align_job) return 'Resource'
			else return  'Comp. A'
		},
		cu_label() {
			if (this.align_job) return 'Comp'
			else return  'Comp. B'
		},

		align_assoc_noun() {
			if (this.align_job) return 'alignment'
			else return  'association'
		},
		align_assoc_noun_uc() {
			if (this.align_job) return 'Alignment'
			else return  'Association'
		},
		align_assoc_adj() {
			if (this.align_job) return 'aligned'
			else return  'associated'
		},
		align_assoc_adj_uc() {
			if (this.align_job) return 'Aligned'
			else return  'Associated'
		},

		resource_cfitems() {
			if (!this.initialized) return {}
			let o = {}
			for (let resource_id of this.resource_ids) {
				o[resource_id] = this.rfr.cfo.cfitems[resource_id]
			}
			return o
		},

		comp_cfitems() {
			if (!this.initialized) return {}
			let o = {}
			for (let comp_id of this.comp_ids) {
				o[comp_id] = this.cfr.cfo.cfitems[comp_id]
			}
			return o
		},

		resource_framework_title() {
			if (!this.rfr) return ''
			// TODO: make this configurable, or get it from the parent of the parent matrix??...
			return this.rfr.json.CFDocument.title
			// let cfi = this.rfr.cfo.cfitems[this.resource_branch]
			// return cfi.fullStatement
		},

		resource_framework_href() {
			return `/${this.resource_framework_id}`
		},

		comp_framework_href() {
			// TODO: get this from the parent of the first competency??
			// return `https://rosetta.commongoodlt.com/${this.comp_framework_id}/${this.comp_branches[0]}`
			// return `https://rosetta.commongoodlt.com/${this.comp_framework_id}`
			return `/${this.comp_framework_id}`
		},

		comp_framework_title() {
			if (!this.cfr) return ''
			if (!this.is_bju_report) {
				return this.cfr.json.CFDocument.title
			}

			let agency = this.cfr.json.CFDocument.creator
			agency = agency.replace(/Department of Education/, '')
			agency = agency.replace(/Department of Public Instruction/, '')
			agency = agency.replace(/Department of Public Instruction/, '')
			agency = agency.trim()

			let subject = U.framework_subject_guess(this.cfr.json.CFDocument)
			if (subject.label == 'Other') {
				subject = this.cfr.json.CFDocument.title.substr(0,75)
				if (subject != this.cfr.json.CFDocument.title) subject += '…'
				let s = subject = `${agency} — ${subject}`
				if (subject.length > 40) {
					s = `<span style="font-size:15px">${s}</span>`
				}
				return s

			} else {
				return `${agency} — ${subject.label}`
			}			
		},

		////////////////////////////////////
		resource_branch_items() {
			let arr = []
			if (!this.resource_universe) return arr

			// use the item type of the first resource branch; assume they are all the same if this computed is being used for something
			if (this.resource_universe?.[0].itemIdentifiers) {
				for (let branch_id of this.resource_universe[0].itemIdentifiers) {
					let item = this.rfr.cfo.cfitems[branch_id]
					if (!item) {
						console.warn('couldn’t find resource_branch for ' + branch_id)
						continue
					}
					arr.push(item)
				}
			}
			return arr
		},

		resource_branch_item_type() {
			if (!this.resource_universe) return ''
			if (this.resource_branch_items.length == 0) return ''
			return U.item_type_string(this.resource_branch_items[0]) || 'Resource Set'
		},

		resource_branch_item_type_plural() {
			if (!this.resource_universe) return ''
			return this.resource_branch_item_type + 's'
		},

		resource_branch_colors() {
			if (this.show_print_view) return ''
			if (this.resource_branch_items.length <= 5) {
				return [
					'deep-purple darken-4',
					'teal darken-4',
					'lime darken-4',
					'orange darken-4',
					'brown darken-4',
				]

			} else {
				let arr = []
				for (let i = 0; i < this.resource_branch_items.length; ++i) {
					arr.push('blue-grey darken-3')
				}
				return arr
			}
		},

		n_resource_branches_showing() {
			let n = 0
			for (let x of this.resource_branch_showing) {
				if (x) n += 1
			}
			return n
		},

		// Currently used in MatrixView TODO: get these from the elements, and/or have them specified in the job
		comp_item_type() { return 'Standard Element' },
		comp_label() { return 'Standard' },
		comp_label_plural() { return 'Standards' },

		resource_item_type() {
			// if we're using aggregation, return resource_aggregation_type as resource_label
			if (!empty(this.resource_aggregation_type)) {
				return this.resource_aggregation_type
			}

			// otherwise look at CFItemTypes
			let types = this.resource_universe[0]?.CFItemTypes
			if (!empty(types) && types.length > 0) {
				return types[0]
			}

			// default: 'Resource'
			return 'Resource'
		},
		resource_label() { return this.resource_item_type },
		resource_label_plural() { return this.resource_label + 's' },

		sub_resource_item_type() {
			// this will only be used if we're using aggregation; use CFItemTypes
			let types = this.resource_universe[0]?.CFItemTypes
			if (!empty(types) && types.length > 0) {
				return types[0]
			}
			return 'Resource'			
		},
		sub_resource_label() { return this.sub_resource_item_type },
		sub_resource_label_plural() { return this.sub_resource_label + 's' },
		
		description_label() { return 'Summary' },
		description_label_plural() { return 'Summaries' },

		///////////////////////////////////
		// "meta-settings" for reports

		is_bju_report() {
			if (!this.rfr) return false
			// TODO: make this configurable, or get it from the parent of the parent matrix??...
			return this.rfr.json.CFDocument.title.includes('BJU')
		},

		report_banner_bgd_color() {
			if (this.is_bju_report) return `#E3F2FD`
			return '#ccc'
		},

		report_logo() {
			// TODO: store this as part of the job
			if (this.is_bju_report) return `/src/logos/bju_press_logo.png`
			return `/images/cglt-globe-with-stars.svg`
		},
		
		report_logo_style() {
			if (this.is_bju_report) return ``
			return `left:-36px; top:-36px; height:100px`
		},
		
		////////////////////////////////
		matrix_stats() {
			return this.job_status?.matrix_stats ?? {}
		},

		auto_alignment_threshold() {
			if (this.align_job) {
				return this.job_status?.auto_alignment_threshold ?? 80
			} else {
				return this.job_status?.auto_alignment_threshold ?? 70
			}
		},

		alignments() {
			let alignments = new Set()
			let resources_aligned = new Set()
			let comps_aligned = new Set()
			let n_primary_alignments = 0
			let n_sme_alignments = 0
			let n_auto_alignments = 0
			let n_not_aligned = 0

			if (this.matrix) {
				for (let comp_id in this.matrix) {
					for (let resource_id in this.matrix[comp_id]) {
						let cell = this.matrix[comp_id][resource_id]

						if (this.cell_is_aligned(cell, this.auto_alignment_threshold)) {
							alignments.add(cell)
							resources_aligned.add(resource_id)
							comps_aligned.add(comp_id)

							if (!empty(cell.sme_alignment)) {
								n_sme_alignments += 1
								if (cell.sme_alignment == 'primary') {
									n_primary_alignments += 1
								}
							} else {
								n_auto_alignments += 1
							}
						}

						if (cell.n_not_aligned) n_not_aligned += 1
					}
				}
			}

			this.resources_aligned = resources_aligned
			this.comps_aligned = comps_aligned

			this.n_primary_alignments = n_primary_alignments
			this.n_sme_alignments = n_sme_alignments
			this.n_auto_alignments = n_auto_alignments
			this.n_not_aligned = n_not_aligned

			return alignments
		},
		n_alignments() {
			return this.alignments.size
		},
		n_resources_aligned() {
			return this.resources_aligned.size
		},
		n_comps_aligned() {
			return this.comps_aligned.size
		},

	},

	methods: {
		async load_data(process_missing = false) {
			let result

			U.loading_start('Compiling Report…')
			// "cache" results in vapp (makes loading faster during development)
			if (false && window.location.toString().includes('localhost') && vapp.report_cram_job_id == this.cram_job_id) {
				result = vapp.cram_report_result

			} else {
				// get the matrix
				let payload = {
					user_id: this.user_info.user_id,
					action: 'get_matrix',
					cram_job_id: this.cram_job_id,
				}

				result = await U.ajax_promise('align/scram', payload)
				// console.log(result)

				if (result.status != 'ok') {
					this.$alert('Error compiling alignment matrix');
					return
				}

				// cache in vapp
				vapp.report_cram_job_id = this.cram_job_id
				vapp.cram_report_result = sc(result)
			}

			// parse job_status and universes
			if (!empty(result.job_status)) {
				this.job_status = result.job_status
				this.resource_universe = JSON.parse(this.job_status.universe_params.resourceUniverse)
				this.comp_universe = JSON.parse(this.job_status.universe_params.competencyUniverse)
				this.resource_aggregation_type = this.job_status.universe_params.resourceAggregationItemType
			}

			// if this job includes parent/child matrices, store like this
			let new_matrix, new_sub_matrix = null
			if (!empty(result.parent_matrix)) {
				new_matrix = result.parent_matrix
				new_sub_matrix = result.matrix
			} else {
				new_matrix = result.matrix
			}

			// if the matrices are read-only, let the component process them now, then freeze them;
			// a frozen matrix is not made reactive by Vue, and cells changed after this point won't trigger updates
			if (this.matrix_read_only) {
				this.prepare_matrices(new_matrix, new_sub_matrix)
				Object.freeze(new_matrix)
				if (new_sub_matrix) Object.freeze(new_sub_matrix)
			}
			this.matrix = new_matrix
			if (new_sub_matrix) this.sub_matrix = new_sub_matrix

			// get all resource and competency framework_ids from matrix
			// TODO: for now we're just supporting one each...
			for (let comp_id in result.matrix) {
				for (let resource_id in result.matrix[comp_id]) {
					if (this.align_job) {
						this.resource_framework_id = result.matrix[comp_id][resource_id].resource_framework_id
						this.comp_framework_id = result.matrix[comp_id][resource_id].competency_framework_id
					} else {
						this.resource_framework_id = result.matrix[comp_id][resource_id].competency_a_framework_id
						this.comp_framework_id = result.matrix[comp_id][resource_id].competency_b_framework_id
					}
					if (!empty(this.resource_framework_id) && !empty(this.comp_framework_id)) {
						break
					}
				}
			}

			if (empty(this.resource_framework_id)) {
				this.$alert('error: resource_framework_id empty')
				return
			}
			if (empty(this.comp_framework_id)) {
				this.$alert('error: comp_framework_id empty')
				return
			}

			// get the resource and comp framework_record
			this.rfr = await U.load_framework_and_cfo(this.resource_framework_id)
			this.cfr = await U.load_framework_and_cfo(this.comp_framework_id)

			// set up resource_branch_showing
			let arr = []
			for (let i = 0; i < this.resource_branch_items.length; ++i) {
				arr.push(true)
			}
			this.resource_branch_showing = arr

			U.loading_stop()
		},

		// hook for components with matrix_read_only: called by load_data with the matrix and sub_matrix (or null)
		// before they are frozen and stored; components can override this to make changes to the cells
		prepare_matrices(matrix, sub_matrix) {},

		get_ancestors(comp_id) {
			let cfi = this.cfr.cfo.cfitems[comp_id]
			if (empty(cfi)) return []	// shouldn't happen
			let node = cfi.tree_nodes[0]?.parent_node
			let arr = []

			// trace up until we hit the document
			while (node && !node.cfitem?.title) {
				arr.unshift(node.cfitem)
				node = node.parent_node
			}

			return arr
		},

		cfitem_title(cfi, max_width = null) {
			let s = cfi.fullStatement
			if (!empty(cfi.humanCodingScheme)) s = `**${cfi.humanCodingScheme}** ${s}`
			if (max_width !== null) {
				let ss = s.substr(0, max_width)
				if (s != ss) ss += '…'
				s = ss
			}

			// the same titles are rendered over and over (e.g. citations), so cache the rendered result by source text
			// (title_cache is deliberately not in data(), so Vue doesn't make it reactive)
			if (!this.title_cache) this.title_cache = new Map()
			let rendered = this.title_cache.get(s)
			if (rendered === undefined) {
				rendered = U.marked_latex(s)
				this.title_cache.set(s, rendered)
			}
			return rendered
		},

		cell_is_aligned(cell, simscore_threshold) {
			if (!empty(cell.sme_alignment)) return true
			if (cell.simscore >= simscore_threshold) return true
		},

		// a and b must be cells of the matrix
		sort_fn(a,b) {
			// for align jobs, put sme_alignments at the top
			if (this.align_job) {
				if (a.sme_alignment && !b.sme_alignment) return -1
				if (b.sme_alignment && !a.sme_alignment) return 1

			// for crosswalk, put sme_assocs at the top
			} else {
				if (a.sme_assoc == 'exactMatchOf' && b.sme_assoc != 'exactMatchOf') return -1
				if (b.sme_assoc == 'exactMatchOf' && a.sme_assoc != 'exactMatchOf') return 1

				if (a.sme_assoc == 'isRelatedTo' && b.sme_assoc != 'isRelatedTo') return -1
				if (b.sme_assoc == 'isRelatedTo' && a.sme_assoc != 'isRelatedTo') return 1
			}

			// else sort by simscore descending
			let simscore_dif = b.simscore - a.simscore
			if (simscore_dif != 0) return simscore_dif

			// else sort by sbcosim descending
			let sbcosim_dif = b.sbcosim - a.sbcosim
			if (sbcosim_dif != 0) return sbcosim_dif

			// if all else fails, sort by id (arbitrary, but consistent)
			if (this.align_job) {
				if (a.resource_id < b.resource_id) return -1
				return 1
			} else {
				if (a.competency_a_id < b.competency_a_id) return -1
				return 1
			}
		},

		simscore_display(cell) {
			let s = cell.simscore
			if (!empty(cell.rc_simscore) && !empty(cell.cc_simscore)) {
				s += ` (${cell.rc_simscore} / ${cell.cc_simscore})`
			}
			return s
		},

		async view_comp_universe() {
			if (!(this.cram_job_id > 0)) {
				this.$alert('You must save the Job to view items in the Competency Universe.')
			}
			if (!(this.matrix_stats?.competencyCount > 0)) {
				this.$alert('No competencies match your current criteria.')
			}

			if (empty(this.matrix)) {
				await this.load_data()
			}

			let s = '<ul>'
			let count = 0
			for (let comp_id in this.matrix) {
				++count
				let cfi
				for (let fr of this.cu_fr) {
					cfi = fr.cfo.cfitems[comp_id]
					if (cfi) break
				}
				if (cfi) {
					s += `<li> <i class="float-right ml-1" style="font-size:12px">[${U.item_type_string(cfi)}]</i> ${this.cfitem_title(cfi, 240)}</li>`
				} else {
					s += `<li>Unknown comp: ${comp_id}</li>`
				}
			}
			s += '</ul>'

			this.$alert({
				title: `Competency Universe Items (${count})`,
				text: s,
				dialogMaxWidth: 800,
			}).then(y => {})
		},

		async view_resource_universe() {
			if (!(this.cram_job_id > 0)) {
				this.$alert('You must save the Job to view items in the Resource Universe.')
			}
			if (!(this.matrix_stats?.resourceCount > 0)) {
				this.$alert('No resources match your current criteria.')
			}

			if (empty(this.matrix)) {
				await this.load_data()
			}

			let s = '<ul>'
			let count = 0
			for (let comp_id in this.matrix) {
				for (let resource_id in this.matrix[comp_id]) {
					++count
					let cfi = this.ru_fr.cfo.cfitems[resource_id]
					if (cfi) {
						s += `<li>${this.cfitem_title(cfi, 240)}</li>`
					} else {
						s += `<li>Unknown resource: ${resource_id}</li>`
					}
				}
				break
			}
			s += '</ul>'

			this.$alert({
				title: `Resource Universe Items (${count})`,
				text: s,
				dialogMaxWidth: 720,
			}).then(y => {})
		},
	}
}
